import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import classnames from "classnames";
import moment from "moment";
import map from "lodash/map";
import filter from "lodash/filter";
import PropTypes from "prop-types";
import * as styles from "./EligibilityTrackerInstructionsPage.scss";

import Button from "components/common/Button";
import Image from "components/common/Image";
import Modal from "components/common/Modal";
import ProgramOptInDetailsModal from "components/clinic/elements/ProgramOptInDetailsModal";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import EligibleClientSearch from "components/eligibilityTracker/EligibleClientSearch";
import HistoricalClientSearch from "components/eligibilityTracker/HistoricalClientSearch";

import {
  getAdminEligibilityTrackerQRCode,
  getEligibilityTrackerQRCode,
} from "api/ClinicApi";
import * as ProgramActions from "actions/ProgramActions";

import logger from "utils/logger";
import { downloadFile, imageFile } from "utils/request";
import {
  PermissionTypes,
  userHasPermission,
} from "utils/permissions/rolesPermissions";

import { ET_FAQ_QUESTIONS } from "constants/ProgramConstants";
import { ENROLLED } from "constants/ProgramStatuses";
import * as UserPermissions from "constants/UserPermissions";
import get from "lodash/get";

function EligibilityTrackerInstructions({
  canEditProgramEnrollmentClinic,
  clinicId,
  hasEligibilityTracker = false,
  canViewEligibilityTrackerAdminQRCode = false,
  canViewEligibilityTrackerHistoryData = false,
  getClinicProgramServiceDetails,
  linkBase,
  programDetails,
  service,
  urlToCouponLibrary,
}) {
  const programId = 7;
  const serviceId = 10;
  const [qrCodeRes, setQRCodeRes] = useState(null);
  const [qrCode, setQRCode] = useState(null);
  const [adminQrCode, setAdminQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showManageEnrollment, setShowManageEnrollment] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  useEffect(() => {
    if (clinicId && getClinicProgramServiceDetails) {
      getClinicProgramServiceDetails(clinicId, programId, serviceId);
    }
  }, [clinicId, getClinicProgramServiceDetails, programId, serviceId]);

  const renderQRCodeMessage = () => {
    const etDate = programDetails?.etStartUsingDate;

    if (!etDate) {
      return (
        <span>
          <b>
            You may start use of your QR code and unique instant rebate code
            beginning tomorrow.
          </b>
        </span>
      );
    }

    const startDate = moment(etDate);
    const currentDate = moment();
    const isAfterStartDate = currentDate.isSameOrAfter(startDate, "day");

    return (
      <span>
        {isAfterStartDate ? (
          "You are now free to use your QR code and unique instant rebate code."
        ) : (
          <span>
            <b>
              You may start use of your QR code and unique instant rebate code
              beginning tomorrow.
            </b>
          </span>
        )}
      </span>
    );
  };

  const faqQuestions = useMemo(() => {
    return map(ET_FAQ_QUESTIONS, (faq, indx) => (
      <div className={styles.faqQuestion} key={`faq_${indx}`}>
        <h3>{faq.q}</h3>
        <div className={styles.faqAnswer}>{faq.a}</div>
      </div>
    ));
  }, []);

  const handleLoadEligibilityTrackerQRCode = () => {
    setLoading(true);
    getEligibilityTrackerQRCode(clinicId)
      .then((res) => {
        setQRCodeRes(res);
        const url = imageFile(res, "jpg", true);
        setQRCode(url);
      })
      .catch((err) => {
        logger.error("Error loading Eligibility Tracker QR Code:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (hasEligibilityTracker && clinicId) {
      handleLoadEligibilityTrackerQRCode();
    }
  }, [hasEligibilityTracker, clinicId]);

  const handleLoadAdminEligibilityTrackerQRCode = () => {
    setLoading(true);
    getAdminEligibilityTrackerQRCode(clinicId)
      .then((res) => {
        const url = imageFile(res, "jpg", true);
        setAdminQrCode(url);
      })
      .catch((err) => {
        logger.error("Error loading Admin Eligibility Tracker QR Code:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (canViewEligibilityTrackerAdminQRCode && clinicId) {
      handleLoadAdminEligibilityTrackerQRCode();
    }
  }, [canViewEligibilityTrackerAdminQRCode, clinicId]);

  const handleManageEnrollment = () => {
    setShowManageEnrollment(true);
  };

  const handleDownloadQRCode = () => {
    downloadFile(qrCodeRes, "jpeg");
  };

  return (
    <>
      <div className="flex flex-1 flex-column padding-top-md">
        <h2>Personalized Offers</h2>
        {canEditProgramEnrollmentClinic && service && (
          <div>
            <div className="flex flex-none align-center full-width">
              <div className="flex-none">
                Enrolled on{" "}
                {moment(service.userActionDate).format("MM/DD/YYYY")} by{" "}
                {service.signature}
              </div>
              <div className="flex-none flex flex-centered no-wrap margin-left-sm">
                <Button small onClick={handleManageEnrollment} type={"default"}>
                  Manage Enrollment
                </Button>
              </div>
            </div>
          </div>
        )}
        <div className="padding-top-md padding-bottom-xlg">
          Congratulations, you have completed the setup for Personalized Offers!{" "}
          {renderQRCodeMessage()} If you have any questions please reach out to
          Greenline support at{" "}
          <a className="text-primary" href="tel:18666089038">
            +1 (866) 608-9038
          </a>
          , email at{" "}
          <a className="text-primary" href="mailto:support@greenlinepet.com">
            support@greenlinepet.com
          </a>
          , or via live chat below!
        </div>
        {clinicId && (
          <>
            <EligibleClientSearch
              urlToCouponLibrary={urlToCouponLibrary || ""}
              clinicId={clinicId}
            />
            {canViewEligibilityTrackerHistoryData && (
              <HistoricalClientSearch clinicId={clinicId} />
            )}
          </>
        )}
        <div className="flex flex-wrap spaced-content spaced-content-vertical">
          <div
            className={classnames(
              styles.tile,
              "flex margin-bottom-sm spaced-content"
            )}
            style={{
              flex: "2 2 0",
              minWidth: "460px",
            }}
          >
            <div className="flex flex-column flex-1 height-100-perc">
              <h3 className="flex-none">Clinic QR Code</h3>
              <div className="flex-none">
                Download the QR code to put in your clinic!
              </div>
              <div className="flex-1 padding-bottom-lg" />
              <div className="flex-none flex flex-centered no-wrap">
                <Button
                  icon
                  large
                  wide
                  onClick={handleDownloadQRCode}
                  disabled={!hasEligibilityTracker}
                >
                  <i className="fa fa-download" /> Download QR Code
                </Button>
              </div>
            </div>
            {!!qrCode ? (
              <div className="flex-column">
                <div className="flex-none flex align-bottom height-100-perc">
                  <Image
                    className={styles.qrCodeImage}
                    src={qrCode}
                    alt="Clinic Eligibility Tracker QR code"
                  />
                </div>
              </div>
            ) : (
              <div className={styles.qrCodePlaceHolder}>
                <span>
                  GENERATING
                  <br /> QR CODE
                </span>
              </div>
            )}
          </div>
          {canViewEligibilityTrackerAdminQRCode && (
            <div className={styles.adminQRCodeTile}>
              {!!adminQrCode ? (
                <div className="flex-column">
                  <h3 className="text-center">Admin QR Code</h3>
                  <div className="flex-none">
                    <Image
                      className={styles.adminQRCodeImage}
                      src={adminQrCode}
                      alt="Admin  QR code"
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.qrCodePlaceHolder}>
                  <span>
                    GENERATING
                    <br /> ADMIN QR CODE
                  </span>
                </div>
              )}
            </div>
          )}

          <div
            className={classnames(
              styles.tile,
              "flex margin-bottom-sm spaced-content"
            )}
            style={{
              flex: "2 2 0",
              minWidth: "460px",
            }}
          >
            <div className="flex flex-column flex-1 height-100-perc">
              <h3 className="flex-none">Clinic Materials</h3>
              <div className="flex-none">
                To order in clinic materials, contact your Boehringer Ingelheim
                Representative.
              </div>
              <div className="flex-1 padding-bottom-lg" />
              <div className="flex-none flex flex-centered no-wrap spaced-content">
                <a
                  className="btn btn-full btn-color-primary btn-icon"
                  href="https://cdn.greenlinepet.com/images/eligibilitytracker/clinic_downloads/2024_Personalized_Offers_in_Clinic_Materials_US-PET-0118-2024-B_Wall_Cling.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa fa-download" /> Preview Wall Cling
                </a>
                <a
                  className="btn btn-full btn-color-primary btn-icon"
                  href="https://cdn.greenlinepet.com/images/eligibilitytracker/clinic_downloads/2024_Personalized_Offers_in_Clinic_Materials_US-PET-0118-2024-A_Counter_Card.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa fa-download" /> Preview Counter Card
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap spaced-content spaced-content-vertical margin-top-sm">
          <Button onClick={() => setShowFAQ(true)} text large type={"blue"}>
            Frequently Asked Questions
          </Button>
        </div>
      </div>
      {clinicId && service && (
        <ProgramOptInDetailsModal
          key={`Program_service_${clinicId}_${programId}_${serviceId}`}
          clinicId={clinicId}
          onClose={() => setShowManageEnrollment(false)}
          program={service}
          programId={serviceId}
          parentProgram={programId}
          show={showManageEnrollment}
          linkBase={linkBase || ""}
        />
      )}
      <Modal
        show={showFAQ}
        modalTitle={"Frequently Asked Questions"}
        medium
        onClose={() => setShowFAQ(false)}
      >
        <div className={styles.faqModal}>
          <div className={styles.faqWrapper}>
            <div className={styles.faqContent}>{faqQuestions}</div>
          </div>
          <div className={styles.faqButtonRow}>
            <Button type={"default"} onClick={() => setShowFAQ(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <SpinnerTakeover show={loading} />
    </>
  );
}

EligibilityTrackerInstructions.propTypes = {
  basePath: PropTypes.string,
  canEditProgramEnrollmentClinic: PropTypes.bool,
  clinicId: PropTypes.number,
  hasEligibilityTracker: PropTypes.bool,
  canViewEligibilityTrackerAdminQRCode: PropTypes.bool,
  canViewEligibilityTrackerHistoryData: PropTypes.bool,
  getClinicProgramServiceDetails: PropTypes.func,
  linkBase: PropTypes.string,
  programDetails: PropTypes.object,
  service: PropTypes.object,
  urlToCouponLibrary: PropTypes.string,
};

export default connect(
  (state, ownProps) => {
    const clinicId = Number(ownProps.clinicId) || undefined;
    const programId = 7;
    const serviceId = 10;

    if (!state || !state.entities) {
      logger.error("Redux state or state.entities is undefined", state);
      return {
        ...ownProps,
        programDetails: null,
        urlToCouponLibrary: ownProps.urlToCouponLibrary || "",
      };
    }

    const clinicProgramServiceDetails = get(
      state,
      `entities.clinicProgramServiceDetails.${clinicId}.${programId}.${serviceId}`,
      null
    );

    const userProfile = get(state, "user.userProfile", {});

    const urlToCouponLibrary = get(state, "system.urlToCouponLibrary", "");

    return {
      ...ownProps,
      programDetails: clinicProgramServiceDetails,
      canViewEligibilityTrackerAdminQRCode: userHasPermission(
        PermissionTypes.VIEW,
        UserPermissions.ELIGIBILITY_TRACKER_ADMIN_QR_CODE,
        userProfile
      ),
      hasEligibilityTracker:
        !!filter(get(state, `entities.clinicPrograms.${clinicId}`, []), {
          programId: programId,
          status: ENROLLED,
        })?.length || false,
      urlToCouponLibrary: urlToCouponLibrary,
    };
  },
  (dispatch) => ({
    getClinicProgramServiceDetails: (clinicId, programId, serviceId) => {
      if (!clinicId) {
        logger.warn(
          "Cannot get program service details, clinicId is undefined"
        );
        return Promise.resolve();
      }
      return dispatch(
        ProgramActions.loadClinicProgramServiceDetails(
          clinicId,
          programId,
          serviceId
        )
      );
    },
  })
)(EligibilityTrackerInstructions);
